import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/Input';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { StepScaffold } from '../components/obros/StepScaffold';
import { useAuthStore } from '../stores/authStore';

// Calca ScreenRegister de screens-auth.jsx (Claude Design): paso 1 (tipo de cuenta + datos), paso 2 (contraseña).
export const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isLoading } = useAuthStore();
  const [step, setStep] = useState<1 | 2>(1);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    user_type: (searchParams.get('type') === 'worker' ? 'worker' : 'client') as 'client' | 'worker',
  });

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    if (formData.password !== formData.password_confirmation) {
      setError('Las contraseñas no coinciden');
      return;
    }
    try {
      await register(formData);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al registrarse');
    }
  };

  return (
    <div className="w-full max-w-lg px-4">
      <div className="bg-surface rounded-[24px] shadow-xl border border-outlineVarWarm overflow-hidden">
        <StepScaffold
          title={step === 1 ? '¿Cómo vas a usar Obros?' : 'Elegí tu contraseña'}
          subtitle={step === 1 ? 'Podés cambiarlo después en tu perfil.' : 'Último paso para crear tu cuenta'}
          step={step}
          totalSteps={2}
          error={error}
          onBack={step === 2 ? () => setStep(1) : undefined}
          onContinue={step === 1 ? () => setStep(2) : handleSubmit}
          continueLabel={step === 1 ? 'Continuar' : 'Crear cuenta'}
          isLoading={isLoading}
        >
          {step === 1 ? (
            <div className="space-y-6">
              <div className="flex flex-col gap-3">
                {[
                  { id: 'client', icon: 'person_search', title: 'Soy cliente', desc: 'Necesito contratar un profesional para un trabajo.' },
                  { id: 'worker', icon: 'engineering', title: 'Soy profesional', desc: 'Ofrezco mis servicios y busco trabajos.' },
                ].map((opt) => {
                  const on = formData.user_type === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => updateField('user_type', opt.id)}
                      className={`flex items-start gap-3.5 p-4 rounded-2xl border-[1.5px] text-left transition-all ${
                        on ? 'bg-cream border-primary-500' : 'bg-surface border-outlineVarWarm hover:border-primary-300'
                      }`}
                    >
                      <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${on ? 'bg-primary-500 text-white' : 'bg-surface-var text-onSurface-var'}`}>
                        <MaterialIcon name={opt.icon} size={22} />
                      </span>
                      <div className="flex-1">
                        <p className="font-bold text-onSurface">{opt.title}</p>
                        <p className="text-[13px] text-onSurface-var leading-snug mt-0.5">{opt.desc}</p>
                      </div>
                      <span
                        className={`w-[22px] h-[22px] mt-1 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          on ? 'bg-primary-500 border-primary-500' : 'border-outlineWarm'
                        }`}
                      >
                        {on && <MaterialIcon name="check" size={14} className="text-white" />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div>
                <h3 className="text-xs font-bold text-onSurface-var uppercase tracking-wide mb-4">Datos personales</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input pill label="Nombre" value={formData.first_name} onChange={(e) => updateField('first_name', e.target.value)} placeholder="Juan" required />
                    <Input pill label="Apellido" value={formData.last_name} onChange={(e) => updateField('last_name', e.target.value)} placeholder="Pérez" required />
                  </div>
                  <Input pill label="Email" type="email" value={formData.email} onChange={(e) => updateField('email', e.target.value)} placeholder="tu@email.com" required />
                  <Input
                    pill
                    label="Teléfono"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="11 2345 6789"
                    helper="Te vamos a enviar un código por WhatsApp"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                pill
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => updateField('password', e.target.value)}
                rightIcon={
                  <button type="button" onClick={() => setShowPassword(!showPassword)}>
                    <MaterialIcon name={showPassword ? 'visibility_off' : 'visibility'} size={18} className="text-onSurface-mute" />
                  </button>
                }
                placeholder="••••••••"
                required
              />
              <Input
                pill
                label="Confirmar contraseña"
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => updateField('password_confirmation', e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
          )}
        </StepScaffold>
      </div>

      <div className="mt-6 text-center text-sm text-onSurface-mute">
        ¿Ya tenés cuenta?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Iniciá sesión
        </Link>
      </div>
    </div>
  );
};
